import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useDemoMode } from "@/pages/demo/DemoModeContext";

export interface DocumentReference {
  id: number;
  document_id: number;
  estate_item_id: number;
  created_at: string;
  review_outcome: string;
  file_name: string | null;
  display_name: string | null;
  document_type: string;
  document_created_at: string;
}

export function useItemDocumentReferences(itemId: number | undefined, contactIds?: number[]) {
  const { isDemoMode } = useDemoMode();
  const effectiveContactIds = isDemoMode ? [] : (contactIds ?? []);

  return useQuery({
    queryKey: ["item-document-references", itemId, effectiveContactIds],
    queryFn: async (): Promise<DocumentReference[]> => {
      const { data: itemRefs, error: itemError } = await supabase
        .from("document_item_references")
        .select(
          `id, document_id, estate_item_id, created_at, review_outcome,
           estate_documents!document_id(file_name, display_name, document_type, created_at)`,
        )
        .eq("estate_item_id", itemId!);

      if (itemError) throw itemError;

      const results = new Map<number, DocumentReference>();

      for (const row of itemRefs ?? []) {
        const doc = Array.isArray(row.estate_documents)
          ? row.estate_documents[0]
          : row.estate_documents;
        results.set(row.document_id, {
          id: row.id,
          document_id: row.document_id,
          estate_item_id: row.estate_item_id,
          created_at: row.created_at,
          review_outcome: row.review_outcome,
          file_name: doc?.file_name ?? null,
          display_name: doc?.display_name ?? null,
          document_type: doc?.document_type ?? "",
          document_created_at: doc?.created_at ?? "",
        });
      }

      if (contactIds && contactIds.length > 0) {
        const { data: contactRefs, error: contactError } = await supabase
          .from("document_contact_references")
          .select(
            `id, document_id, contact_id, created_at, review_outcome,
             estate_documents!document_contact_references_document_id_fkey(file_name, display_name, document_type, created_at)`,
          )
          .in("contact_id", contactIds);

        if (contactError) throw contactError;

        for (const row of contactRefs ?? []) {
          if (results.has(row.document_id)) continue;
          const doc = Array.isArray(row.estate_documents)
            ? row.estate_documents[0]
            : row.estate_documents;
          results.set(row.document_id, {
            id: row.id,
            document_id: row.document_id,
            estate_item_id: itemId!,
            created_at: row.created_at,
            review_outcome: row.review_outcome,
            file_name: doc?.file_name ?? null,
            display_name: doc?.display_name ?? null,
            document_type: doc?.document_type ?? "",
            document_created_at: doc?.created_at ?? "",
          });
        }
      }

      return Array.from(results.values());
    },
    enabled: itemId !== undefined && !isDemoMode,
    staleTime: isDemoMode ? Infinity : 60_000,
  });
}
